/**
 * Confirmação de e-mail no cadastro — lógica PURA, sem React e sem Supabase.
 *
 * ── O QUE MUDA QUANDO O TOGGLE LIGA ─────────────────────────────────────────
 * Medido em 11/08: 152 cadastros por `email` em 7 dias, 152 de 152 com
 * `email_confirmed_at` a menos de 5 s de `created_at` — auto-confirm em 100%.
 * Com "Confirm email" DESLIGADO, `supabase.auth.signUp` devolve `user` E
 * `session`, e o cadastro segue direto para o onboarding.
 *
 * Ligado o toggle, o MESMO `signUp` devolve `user` e `session: null`. Nada
 * lança. O `error` é null. Quem assume sessão não recebe erro nenhum — recebe
 * um caminho que simplesmente não funciona mais, e em silêncio.
 *
 * ── POR QUE A DECISÃO É UMA FUNÇÃO PURA ─────────────────────────────────────
 * Porque a exigência nº 6 ("conta já confirmada que faz login normal não pode
 * cair na tela nova em NENHUMA hipótese") é uma afirmação sobre a decisão, não
 * sobre a tela. Testar isso pela UI provaria um caminho; testar aqui prova o
 * conjunto — inclusive as formas de resposta que só aparecem com proteção de
 * enumeração ligada, que é onde mora a armadilha.
 */

/** Convenção viva do app: o mesmo destino que o OAuth já usa. */
const NATIVE_AUTH_CALLBACK = "com.aivyv.mirror://auth/callback";
const WEB_AUTH_CALLBACK_PATH = "/auth/callback";

/**
 * Para onde o link de confirmação volta.
 *
 * REUSA o corredor do OAuth de propósito. O link de confirmação do Supabase em
 * PKCE volta com `?code=`, exatamente a forma que o `appUrlOpen` de
 * App.tsx:196 já filtra e que o `exchangeNativeOAuthOnce` já troca por sessão.
 * Um esquema novo exigiria um segundo listener, um segundo Info.plist e um
 * segundo lugar para divergir — e o `/auth/callback` já resolve consentimento
 * e roteamento de perfil para quem chega com sessão.
 *
 * O literal nativo é o mesmo de `NATIVE_OAUTH_REDIRECT` (Auth.tsx:29). Os dois
 * ficam PINADOS por src/test/emailConfirmation.test.ts, que lê o fonte do
 * Auth.tsx e falha se divergirem — mesma doutrina do espelho de regex do
 * animationClipPolicy. Constante duplicada que ninguém confere é como 47 URLs
 * ficaram em 404 com o teste verde.
 */
export function authCallbackRedirectTo(opts: {
  native: boolean;
  origin?: string | null;
}): string {
  if (opts.native) return NATIVE_AUTH_CALLBACK;
  const origin = opts.origin && opts.origin.length > 0 ? opts.origin : "https://www.aivyv.com";
  return origin + WEB_AUTH_CALLBACK_PATH;
}

// ── Decisão do cadastro ─────────────────────────────────────────────────────

export type SignupOutcome =
  /** Veio sessão — o toggle está desligado, segue o caminho de sempre. */
  | { kind: "session" }
  /** Usuário criado, SEM sessão — precisa confirmar o e-mail. */
  | { kind: "needs_confirmation" }
  /** Já existe conta com este e-mail — isto é um LOGIN, nunca um cadastro. */
  | { kind: "existing_account" }
  /** Falhou de verdade — o chamador trata como erro. */
  | { kind: "failed" };

export interface SignupResultLike {
  data?: {
    user?: { identities?: unknown[] | null } | null;
    session?: unknown;
  } | null;
  error?: { code?: string | null; message?: string | null } | null;
}

/**
 * O que aconteceu no `signUp`?
 *
 * A ORDEM é o contrato, e ela existe para a exigência nº 6:
 *
 *  1. CONTA EXISTENTE PRIMEIRO. Com proteção de enumeração ligada, o Supabase
 *     responde a um e-mail já cadastrado com um SUCESSO FALSO: `error` null,
 *     `session` null e um `user` com `identities: []`. Essa resposta é
 *     indistinguível de um cadastro novo pendente de confirmação se olhada só
 *     por "tem user e não tem sessão" — e mandaria uma conta JÁ CONFIRMADA
 *     para a tela de confirmação, que é exatamente o que não pode acontecer.
 *     Por isso `identities: []` é testado ANTES, não depois.
 *  2. Erro real → `failed`.
 *  3. Sessão presente → `session` (toggle desligado, ou provider social).
 *  4. Usuário sem sessão → `needs_confirmation`.
 *  5. Nem usuário nem sessão → `failed`, nunca "confirme seu e-mail": sem
 *     usuário não há nada a confirmar, e a tela mentiria.
 */
export function decideSignupOutcome(res: SignupResultLike): SignupOutcome {
  const error = res.error ?? null;
  const user = res.data?.user ?? null;
  const session = res.data?.session ?? null;

  // 1 — conta que já existe, nas duas formas que o Supabase usa.
  const code = error?.code ?? "";
  if (code === "user_already_exists" || code === "email_exists") {
    return { kind: "existing_account" };
  }
  if (error && /already registered/i.test(error.message ?? "")) {
    return { kind: "existing_account" };
  }
  if (!error && user && Array.isArray(user.identities) && user.identities.length === 0) {
    return { kind: "existing_account" };
  }

  // 2 — erro real.
  if (error) return { kind: "failed" };

  // 3 — sessão veio: nada muda.
  if (session) return { kind: "session" };

  // 4 — criado, aguardando confirmação.
  if (user) return { kind: "needs_confirmation" };

  // 5 — resposta sem nada dentro.
  return { kind: "failed" };
}

// ── Trava de reenvio ────────────────────────────────────────────────────────

/**
 * Janela mínima entre reenvios.
 *
 * Espelha o limite que o Supabase já aplica no servidor (60 s por padrão).
 * A trava do cliente NÃO é a segurança — a segurança é a do servidor; esta
 * existe para o usuário ver um contador em vez de tomar um 429 na cara.
 */
export const RESEND_COOLDOWN_MS = 60_000;

const PENDING_KEY = "vyv_email_confirmation_pending";

export interface PendingConfirmation {
  email: string;
  /** Epoch ms a partir do qual reenviar é permitido. */
  resendableAt: number;
}

/**
 * Persistido porque a trava tem que sobreviver a recarregar a página — senão
 * ela não é trava, é enfeite: F5 e o botão volta a ficar disponível.
 * Guarda o e-mail que o próprio usuário acabou de digitar, no aparelho dele.
 */
export function writePendingConfirmation(p: PendingConfirmation): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(p));
  } catch {
    // Safari privado / storage cheio: a tela ainda funciona pelo state de
    // navegação, e o limite do servidor continua valendo.
  }
}

export function readPendingConfirmation(): PendingConfirmation | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { email?: unknown; resendableAt?: unknown };
    if (typeof p.email !== "string" || p.email === "") return null;
    return {
      email: p.email,
      resendableAt: typeof p.resendableAt === "number" ? p.resendableAt : 0,
    };
  } catch {
    return null;
  }
}

export function clearPendingConfirmation(): void {
  try { localStorage.removeItem(PENDING_KEY); } catch { /* idem */ }
}

/** Quantos ms faltam para poder reenviar. 0 = pode agora. */
export function cooldownRemainingMs(
  p: PendingConfirmation | null,
  now: number,
): number {
  if (!p) return 0;
  const restante = p.resendableAt - now;
  // Relógio adiantado no aparelho não pode travar o botão para sempre: um
  // `resendableAt` absurdamente no futuro é tratado como janela cheia, nunca
  // como bloqueio permanente.
  if (restante > RESEND_COOLDOWN_MS) return RESEND_COOLDOWN_MS;
  return restante > 0 ? restante : 0;
}

/** Segundos exibidos no contador — sempre >= 1 enquanto ainda houver espera. */
export function cooldownSeconds(remainingMs: number): number {
  return remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0;
}
